import { Calendar as ANTCalendar, Flex } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useState } from "react";
import ChevronLeft from "../../../../assets/icons/ChevronLeft";
import ChevronRight from "../../../../assets/icons/ChevronRight";
import WalletIcon from "../../../../assets/icons/WalletIcon";
import { DB_DATE_FORMAT } from "../../../../constants/global.constants";
import {
  useGetDateWiseAmountsQuery,
  useGetPayableDatesQuery,
} from "../../../../features/Dashboard/dashboard.api";
import { cn } from "../../../../utils/string";
import { formatNumber } from "../Dashboard";

const Calendar = ({ filter }: { filter: { year: number; month: number } }) => {
  const [value, setValue] = useState<Dayjs>(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const { data: payableDates } = useGetPayableDatesQuery({ params: {} });

  const { data: dateWiseAmounts } = useGetDateWiseAmountsQuery({
    params: { date: dayjs(selectedDate).format(DB_DATE_FORMAT) },
  });

  // new way: cellRender
  const cellRender = (current: Dayjs, info: { type: string }) => {
    if (info.type === "date") {
      const dateStr = current.format("YYYY-MM-DD");
      const isMarked = payableDates?.data?.payable_dates.includes(dateStr);
      const isSelected = selectedDate?.isSame(current, "day");
      const isPrevDate = value.isBefore(dayjs().startOf("month"), "month");
      const isOtherMonth = current.month() !== value.month();

      return (
        <Flex align="center" justify="center">
          <Flex
            align="center"
            justify="center"
            className={cn(
              "text-base text-theme-slate-800 text-center size-9 rounded-lg relative",
              isSelected
                ? "bg-theme-primaryRest border border-theme-indigo-50 !text-theme-primary after:absolute after:left-2.5 after:right-2.5"
                : "",
              isMarked
                ? "after:absolute after:left-0 after:right-0 after:bottom-1 after:h-1 after:bg-theme-primary after:rounded-full after:w-3 after:mx-auto"
                : "",
              isPrevDate || isOtherMonth ? "opacity-50" : "",
            )}
          >
            {current.date()}
          </Flex>
        </Flex>
      );
    }
    return (
      <div className={cn("text-base text-theme-slate-800 text-center")}>
        {current.date()}
      </div>
    );
  };

  return (
    <div className="p-5 border border-theme-indigo-50 rounded-xl dashboard-calendar">
      <ANTCalendar
        fullscreen={true}
        value={value}
        onSelect={(date) => {
          setSelectedDate(date);
          setValue(date);
        }}
        onPanelChange={(date) => {
          setSelectedDate(date);
          setValue(date);
        }}
        fullCellRender={cellRender}
        headerRender={() => {
          const monthName = value.format("MMMM YYYY");

          return (
            <>
              <Flex
                align="center"
                justify="center"
                gap={24}
                className="text-theme-slate-800 pb-3 border-b border-b-indigo-50"
              >
                <button onClick={() => setValue(value.subtract(1, "month"))}>
                  <ChevronLeft />
                </button>
                <span className="text-base font-medium w-full max-w-[170px] text-center">
                  {monthName}
                </span>
                <button onClick={() => setValue(value.add(1, "month"))}>
                  <ChevronRight />
                </button>
              </Flex>
              {/* Custom Weekday Row */}
              <div className="grid grid-cols-7 text-center py-2 border-b border-indigo-50 text-sm font-medium text-theme-slate-400 uppercase">
                <div>Sun</div>
                <div>Mon</div>
                <div>Tue</div>
                <div>Wed</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
              </div>
            </>
          );
        }}
      />
      <div className="space-y-2">
        <Flex
          align="center"
          justify="space-between"
          className="p-4 rounded-xl bg-theme-error-25 border border-theme-error-50 cursor-pointer"
        >
          <div>
            <span className="text-sm text-theme-error-500">Over Due (BDT)</span>
            <h4 className="text-xl text-theme-error-500 font-semibold">
              {formatNumber(
                dateWiseAmounts?.data?.processedData?.overdue_amount || 0,
              )}
            </h4>
          </div>
          <Flex
            align="center"
            justify="center"
            className="size-8 rounded-lg bg-theme-error-500 text-white"
          >
            <WalletIcon />
          </Flex>
        </Flex>
        <Flex
          align="center"
          justify="space-between"
          className="p-4 rounded-xl bg-theme-purple-25 border border-theme-purple-50 cursor-pointer"
        >
          <div>
            <span className="text-sm text-theme-purple-400">
              Today’s Due (BDT)
            </span>
            <h4 className="text-xl text-theme-purple-400 font-semibold">
              {formatNumber(
                dateWiseAmounts?.data?.processedData?.today_due_amount || 0,
              )}
            </h4>
          </div>
          <Flex
            align="center"
            justify="center"
            className="size-8 rounded-lg bg-theme-purple-400 text-white"
          >
            <WalletIcon />
          </Flex>
        </Flex>
        <Flex
          align="center"
          justify="space-between"
          className="p-4 rounded-xl bg-theme-success-25 border border-theme-success-50 cursor-pointer"
        >
          <div>
            <span className="text-sm text-theme-success-500">
              Pending for Approval
            </span>
            <h4 className="text-xl text-theme-success-500 font-semibold">
              {formatNumber(
                dateWiseAmounts?.data?.processedData?.pending_approval_count ||
                  0,
              )}
            </h4>
          </div>
          <Flex
            align="center"
            justify="center"
            className="size-8 rounded-lg bg-theme-success-500 text-white"
          >
            <WalletIcon />
          </Flex>
        </Flex>
      </div>
    </div>
  );
};

export default Calendar;
